"use client";

import { ScrollArea as ScrollAreaPrimitive } from "@base-ui/react/scroll-area";
import { cn } from "@timeghost/ui/utils";
import type { Ref } from "react";

type ScrollAreaProps = ScrollAreaPrimitive.Root.Props & {
	viewportClassName?: string;
	viewportRef?: Ref<HTMLDivElement>;
};

function ScrollArea({ className, children, viewportClassName, viewportRef, ...props }: ScrollAreaProps) {
	return (
		<ScrollAreaPrimitive.Root
			className={cn("group/scroll-area relative", className)}
			data-slot="scroll-area"
			{...props}>
			<ScrollAreaPrimitive.Viewport
				className={cn(
					"size-full rounded-[inherit] outline-none transition-[color,box-shadow] focus-visible:outline-1 focus-visible:ring-[3px] focus-visible:ring-ring/50",
					viewportClassName
				)}
				data-slot="scroll-area-viewport"
				ref={viewportRef}
				tabIndex={-1}>
				{children}
			</ScrollAreaPrimitive.Viewport>
			<ScrollBar />
			<ScrollBar orientation="horizontal" />
			<ScrollAreaPrimitive.Corner />
		</ScrollAreaPrimitive.Root>
	);
}

function ScrollBar({ className, orientation = "vertical", ...props }: ScrollAreaPrimitive.Scrollbar.Props) {
	return (
		<ScrollAreaPrimitive.Scrollbar
			className={cn(
				"pointer-events-none flex touch-none select-none p-px opacity-0 transition-opacity duration-150",
				"data-hovering:pointer-events-auto data-hovering:opacity-100",
				"data-scrolling:pointer-events-auto data-scrolling:opacity-100 data-scrolling:duration-0",
				"group-focus-within/scroll-area:pointer-events-auto group-focus-within/scroll-area:opacity-100",
				"data-horizontal:h-1.5 data-horizontal:w-full data-horizontal:flex-col",
				"data-vertical:h-full data-vertical:w-1.5",
				className
			)}
			data-orientation={orientation}
			data-slot="scroll-area-scrollbar"
			orientation={orientation}
			{...props}>
			<ScrollAreaPrimitive.Thumb
				className="relative flex-1 rounded-full bg-border hover:bg-muted-foreground/50 active:bg-muted-foreground"
				data-slot="scroll-area-thumb"
			/>
		</ScrollAreaPrimitive.Scrollbar>
	);
}

export { ScrollArea, ScrollBar };
